import clsx from 'clsx'
import { type ChangeEvent, useMemo, useState } from 'react'
import toast from 'react-hot-toast'

import { FvButton, Icon, OrDivider, TextField } from '@fv/client-components'

import { supportMessage } from '@/constants'
import { dateOptions } from '@/features/date-range/types'
import { useUpdateSavedView } from '@/features/shipment-filters/mutations'
import { useShipmentFilterListContext } from '@/features/shipment-filters/ShipmentFilterListProvider'
import { type FilterBase, getFilterQueryString } from '@/types/SavedFilter'

import { DividedContent } from '../DividedContent'
import { ValidatedForm } from '../inputs'
import { ActiveFiltersSummary } from './ActiveFiltersSummary'
import { FilterCard } from './FilterCard'
import { DATE_FILTER_NAMES, type FilterConfig, hasFilterValues } from './types'

type CustomFilterFormProps = {
  onClose: () => void
  onFilter: (qs: string) => void
  editing?: FilterBase
}

export const CustomFilterForm = ({
  onClose,
  onFilter,
  editing,
}: CustomFilterFormProps) => {
  const [showFilterSave, setShowFilterSave] = useState(false)
  const [viewName, setViewName] = useState(editing?.name ?? '')
  const [openDatePicker, setOpenDatePicker] = useState<string | null>(null)
  const [enabledFilters, setEnabledFilters] = useState<Record<string, boolean>>(
    {},
  )
  const updateSavedView = useUpdateSavedView()
  const isUpdating = updateSavedView.isPending
  const { filterConfig } = useShipmentFilterListContext(s => s)
  const listName = useShipmentFilterListContext(s => s.listType)

  const [queryString, setQueryString] = useState(() => {
    if (!editing) return ''
    const qs = new URLSearchParams(getFilterQueryString(editing))
    qs.delete('view') // viewId added by API
    return qs.toString()
  })

  const handleSave = async () => {
    if (isUpdating) return
    if (!validateQueryString(queryString, filterConfig)) return

    const isSystemFilter = editing?.isSystemFilter
    const trimmedViewName = viewName.trim()
    try {
      const result = await updateSavedView.mutateAsync({
        pageName: listName,
        queryString: queryString,
        viewName: trimmedViewName,
        ...(!isSystemFilter && { viewId: editing?._id }),
        ...(isSystemFilter && { systemFilterId: editing?._id }),
      })
      const resultViews = result[listName]
      const filter = resultViews.find(v => v.name === trimmedViewName)
      onFilter(filter?.queryString ?? '')
      toast.success('Filter saved successfully.')
      onClose()
    } catch (e: any) {
      toast.error(e?.message || `Unable to save filter, ${supportMessage}`)
    }
  }

  const filterColumns = useMemo(() => {
    const column1Filters = [
      ...filterConfig.filter(f => f.category === 'date'),
      ...filterConfig.filter(
        f => f.category === 'origin' || f.category === 'dest',
      ),
    ]

    const remainingFilters = filterConfig.filter(f => !f.category)

    const midPoint = Math.ceil(remainingFilters.length / 2)

    return {
      column1: column1Filters,
      column2: remainingFilters.slice(0, midPoint),
      column3: remainingFilters.slice(midPoint),
    }
  }, [filterConfig])

  const columnClasses = 'px-4 py-6 flex flex-col gap-3'

  const renderColumn = (filters: FilterConfig[], bgClass: string) => (
    <div className={clsx(columnClasses, bgClass)}>
      {filters.map((filter, index) => (
        <FilterCard
          key={filter.name}
          filter={filter}
          index={index}
          queryString={queryString}
          setQueryString={setQueryString}
          openDatePicker={openDatePicker}
          setOpenDatePicker={setOpenDatePicker}
          enabledFilters={enabledFilters}
          setEnabledFilters={setEnabledFilters}
        />
      ))}
    </div>
  )

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <DividedContent className="bg-fv-beer-light px-6 py-3 border-b border-fv-beer-medium flex items-center">
        <p className="mb-0 gap-x-1 flex">
          <Icon
            icon="filter"
            className="text-fv-gray-dark"
            transform="down-2"
          />
          Check which filters to apply to your shipments.
        </p>
      </DividedContent>

      <div className="overflow-auto flex-1 flex flex-col">
        <div className="grid grid-cols-[23rem_minmax(0,1fr)_minmax(0,1fr)] gap-0 divide-x divide-fv-blue-300 flex-1">
          {renderColumn(filterColumns.column1, 'bg-fv-blue/5')}
          {renderColumn(filterColumns.column2, 'bg-fv-blue/10')}
          {renderColumn(filterColumns.column3, 'bg-fv-blue/15')}
        </div>
      </div>

      <div className="mt-auto bg-fv-blue-100 p-4 border-t border-fv-blue-200">
        <ActiveFiltersSummary
          queryString={queryString}
          filterConfig={filterConfig}
        />
        {!showFilterSave ? (
          <div className="flex items-center gap-x-2">
            <FvButton
              icon="filter"
              theme="primary"
              onClick={() => setShowFilterSave(true)}
              transform="up-1"
            >
              Filter shipments & save this filter
            </FvButton>
            <OrDivider />

            <FvButton
              theme="default"
              icon="filter"
              transform="up-1"
              onClick={() => {
                if (!validateQueryString(queryString, filterConfig)) return
                onFilter(`${queryString}&view=custom`)
                onClose()
              }}
            >
              Filter shipments
            </FvButton>
            <FvButton theme="cancel" onClick={onClose} icon="times">
              Cancel
            </FvButton>
          </div>
        ) : (
          <ValidatedForm onValidSubmit={handleSave}>
            <label htmlFor="viewName" className="label">
              Name this reusable filter
            </label>
            <TextField
              autoFocus
              className="form-control mb-2"
              name="viewName"
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setViewName(e.target.value)
              }}
              placeholder="i.e. Delivered yesterday"
              readOnly={isUpdating}
              required
              value={viewName}
            />
            <div className="flex fa-width-auto">
              <FvButton
                type="submit"
                theme="primary"
                icon="check"
                loading={isUpdating}
              >
                Save and filter
              </FvButton>
              <FvButton
                theme="plain"
                loading={isUpdating}
                onClick={() => setShowFilterSave(false)}
                icon="times"
              >
                Cancel
              </FvButton>
            </div>
          </ValidatedForm>
        )}
      </div>
    </div>
  )
}

function validateCustomDateRange(range: string) {
  const [start, end] = range.split('-')
  const startDate = Number(start)
  const endDate = Number(end)

  if (!startDate || !endDate) {
    toast.error('Date range must include a start and end date.')
    return false
  }

  return true
}

function validateQueryString(qs: string, filterConfig: FilterConfig[]) {
  if (
    !qs ||
    !filterConfig.some(f => hasFilterValues(f, new URLSearchParams(qs)))
  ) {
    toast.error('Must select at least one filter.')
    return false
  }

  const params = new URLSearchParams(qs)
  for (const name of DATE_FILTER_NAMES) {
    const value = params.get(name)
    if (value && !dateOptions.find(o => o.value === value)) {
      if (!validateCustomDateRange(value)) return false
    }
  }

  return true
}

export default CustomFilterForm
